import { supabase } from './supabase';

class Auth {
  async register(email, password) {
    try {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) {
        console.error('Registration error:', error);
        throw error;
      }
      
      // Check if email confirmation is required
      if (data?.user && data.user.identities?.length === 0) {
        throw new Error('User already registered');
      }
      
      // Store user in localStorage temporarily
      if (data.user) {
        localStorage.setItem('ix_study_user', JSON.stringify(data.user));
      }
      
      return data.user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }
  
  async login(email, password) {
    try {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }
      
      // Bypass email verification for test account
      if (email === 'nr7isgoat@proton.me') {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          // If the test account doesn't exist, create it
          if (error.message.includes('Invalid login credentials')) {
            const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
              email,
              password,
              options: {
                emailRedirectTo: `${window.location.origin}/dashboard`
              }
            });
            
            if (signUpError) {
              console.error('Test account creation error:', signUpError);
              throw signUpError;
            }
            
            // Store user in localStorage
            if (signUpData.user) {
              localStorage.setItem('ix_study_user', JSON.stringify(signUpData.user));
              return signUpData.user;
            }
          }
          throw error;
        }
        
        // Store user in localStorage
        if (data.user) {
          localStorage.setItem('ix_study_user', JSON.stringify(data.user));
        }
        
        return data.user;
      }
      
      // Normal login flow for other accounts
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('Login error:', error);
        throw error;
      }
      
      // Store user in localStorage
      if (data.user) {
        localStorage.setItem('ix_study_user', JSON.stringify(data.user));
      }
      
      return data.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }
  
  async logout() {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Logout error:', error);
        throw error;
      }
      
      // Remove user from localStorage
      localStorage.removeItem('ix_study_user');
      
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }
  
  async resetPassword(email) {
    try {
      if (!email) {
        throw new Error('Email is required');
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        console.error('Reset password error:', error);
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }
  
  async updatePassword(password) {
    try {
      if (!password) {
        throw new Error('New password is required');
      }
      
      const { error } = await supabase.auth.updateUser({
        password,
      });
      
      if (error) {
        console.error('Update password error:', error);
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  }
  
  async getCurrentUser() {
    try {
      // First check if we have a session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Get session error:', error);
        throw error;
      }
      
      // If we have a session, return the user
      if (session?.user) {
        return session.user;
      }
      
      // Check localstorage as fallback
      const storedUser = localStorage.getItem('ix_study_user');
      if (storedUser) {
        return JSON.parse(storedUser);
      }
      
      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }
  
  async refreshSession() {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Refresh session error:', error);
        throw error;
      }
      
      // Update user in localStorage
      if (data.user) {
        localStorage.setItem('ix_study_user', JSON.stringify(data.user));
      }
      
      return data.user;
    } catch (error) {
      console.error('Refresh session error:', error);
      return null;
    }
  }
}

export const auth = new Auth();